import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Music2, LogOut, User, ChevronDown, Disc3, Radio, Layers, Sparkles, Crown, Map } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import LimitedTimeBanner from "./LimitedTimeBanner";
import LanguageSelector from "./LanguageSelector";
import GlobalSearch from "./GlobalSearch";
import CircularBassClef from "./CircularBassClef";

const RIGHTS_HUBS = [
  { slug: "master", label: "Master Rights", icon: Layers, desc: "Ownership · Licensing · Sync" },
  { slug: "digital", label: "Digital Rights", icon: Radio, desc: "Streaming · Downloads · DSPs" },
  { slug: "recording", label: "Recording Rights", icon: Disc3, desc: "Performers · Neighbouring" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  const linkClass = ({ isActive }) =>
    `text-sm tracking-tight transition-colors px-3 py-2 rounded-md ${
      isActive ? "text-[#7DA3D9]" : "text-slate-300 hover:text-white"
    }`;

  const rightsActive = location.pathname.startsWith("/rights/");

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0A1128]/80 border-b border-white/[0.06] saturate-150"
      data-testid="site-header"
    >
      <LimitedTimeBanner />
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <CircularBassClef size={40} />
          <div>
            <span className="font-display text-xl tracking-tight">OSRR</span>
            <span className="font-display text-[10px] text-[#D4AF37] block leading-none mt-0.5 tracking-[0.2em] font-semibold">
              INTERNATIONAL MUSIC ROLODEX
            </span>
          </div>
        </Link>

        {user && (
          <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
            <NavLink to="/agencies" className={linkClass} data-testid="nav-agencies">Agency Directory</NavLink>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`text-sm tracking-tight transition-colors px-3 py-2 rounded-md inline-flex items-center gap-1 outline-none ${
                    rightsActive ? "text-[#7DA3D9]" : "text-slate-300 hover:text-white"
                  }`}
                  data-testid="nav-rights-trigger"
                >
                  Rights <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="bg-[#0A1128]/95 backdrop-blur-xl border-white/10 min-w-[260px] p-2"
                align="start"
                sideOffset={8}
              >
                {RIGHTS_HUBS.map((h) => {
                  const HIcon = h.icon;
                  return (
                    <DropdownMenuItem
                      key={h.slug}
                      onClick={() => nav(`/rights/${h.slug}`)}
                      className="flex items-start gap-3 px-3 py-3 rounded-md cursor-pointer focus:bg-white/[0.04] data-[highlighted]:bg-white/[0.04]"
                      data-testid={`nav-rights-${h.slug}`}
                    >
                      <div className="w-8 h-8 rounded-md border border-[#7DA3D9]/30 bg-[#7DA3D9]/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <HIcon className="w-4 h-4 text-[#7DA3D9]" />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-white">{h.label}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{h.desc}</div>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <NavLink to="/rights-library" className={linkClass} data-testid="nav-rights-library">Library</NavLink>
            <NavLink to="/translate" className={linkClass} data-testid="nav-translate">Translate</NavLink>
          </nav>
        )}

        {!user && (
          <nav className="hidden md:flex items-center gap-1" data-testid="public-nav">
            <Link
              to="/legal/terms"
              className="text-sm tracking-tight transition-colors px-3 py-2 rounded-md text-slate-300 hover:text-white"
              data-testid="public-nav-terms"
            >
              Terms
            </Link>
            <Link
              to="/legal/privacy"
              className="text-sm tracking-tight transition-colors px-3 py-2 rounded-md text-slate-300 hover:text-white"
              data-testid="public-nav-privacy"
            >
              Privacy
            </Link>
          </nav>
        )}

        <div className="flex items-center gap-3">
          <Link
            to="/sitemap"
            className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] hover:border-[#7DA3D9]/40 transition-colors text-xs text-slate-300 hover:text-white font-medium"
            data-testid="nav-sitemap"
            title="Site map · Table of contents"
          >
            <Map className="w-3.5 h-3.5 text-[#7DA3D9]" />
            Site Map
          </Link>
          {user && <GlobalSearch />}
          <LanguageSelector />
          {user ? (
            <>
              {user.has_complete_access ? (
                <span
                  className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#7DA3D9]/30 bg-[#7DA3D9]/8 text-[10px] uppercase tracking-[0.2em] text-[#B8D0EE] font-semibold"
                  data-testid="badge-complete-access"
                  title="Complete Access member"
                >
                  <Crown className="w-3 h-3" /> Member
                </span>
              ) : (
                <Link
                  to="/upgrade"
                  className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#7DA3D9]/40 bg-[#7DA3D9]/5 text-xs text-[#B8D0EE] hover:bg-[#7DA3D9]/12 hover:border-[#7DA3D9] transition-colors font-semibold"
                  data-testid="nav-upgrade"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Unlock · $6.95
                </Link>
              )}
              <Link
                to="/profile"
                className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-white/5 transition-colors"
                data-testid="nav-profile"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7DA3D9]/40 to-[#1E293B] flex items-center justify-center border border-white/10">
                  <User className="w-4 h-4 text-[#B8D0EE]" />
                </div>
                <span className="text-sm hidden sm:inline">{user.name?.split(" ")[0]}</span>
              </Link>
              <button
                onClick={handleLogout}
                className="text-slate-400 hover:text-white p-2 rounded-md hover:bg-white/5 transition-colors"
                data-testid="logout-btn"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-slate-300 hover:text-white px-4 py-2 transition-colors"
                data-testid="login-link"
              >
                Sign in
              </Link>
              <Link to="/register" className="btn-gold text-sm" data-testid="register-link">
                Access registry
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
